import type { ManualBodySection } from "@arc/contracts";

/** Where a dragged step lands: next to another step, or at the end of a section (dropped on its header). */
export type StepDropTarget = { kind: "step"; stepId: number; before: boolean } | { kind: "section"; sectionId: number };

export type StepDropWrite = { kind: "reorder"; sectionId: number; order: number[] } | { kind: "move"; toSectionId: number; order: number[] };

/**
 * A manual usable only in the 2D web view shows every step open (owner 2026-11-03): its authors do not work in 3D, and they
 * navigate by scrolling through the content, not by opening one step at a time.
 */
export function opensAllSteps(platforms: { is2dWebManual: boolean; is3dWebManual: boolean; isVrManual: boolean; isArManual: boolean } | undefined): boolean {
  return platforms != null && platforms.is2dWebManual && !platforms.is3dWebManual && !platforms.isVrManual && !platforms.isArManual;
}

/**
 * The steps of a section the search leaves visible. The open step always stays: only the open card is mounted and it holds
 * unsaved typing, so searching must not unmount it past the flush barrier.
 */
export function stepsForQuery<T extends { id: number; name: string }>(section: { name: string; steps: T[] }, query: string, openStepId: number | null): T[] {
  const q = query.trim().toLowerCase();
  if (q === "" || section.name.toLowerCase().includes(q)) return section.steps;
  return section.steps.filter((st) => st.id === openStepId || st.name.toLowerCase().includes(q));
}

/**
 * The write a step drop means, or `null` when it changes nothing. Both endpoints want the WHOLE ordered id list of the
 * target section (the server's permutation check), so that list is what this builds.
 */
export function resolveStepDrop(sections: ManualBodySection[], stepId: number, target: StepDropTarget): StepDropWrite | null {
  const from = sections.find((s) => s.steps.some((st) => st.id === stepId));
  const to =
    target.kind === "section" ? sections.find((s) => s.id === target.sectionId) : sections.find((s) => s.steps.some((st) => st.id === target.stepId));
  if (!from || !to) return null;
  if (target.kind === "step" && target.stepId === stepId) return null;

  const current = to.steps.map((st) => st.id);
  const order = current.filter((id) => id !== stepId);
  const index = target.kind === "section" ? order.length : order.indexOf(target.stepId) + (target.before ? 0 : 1);
  order.splice(index, 0, stepId);

  if (from.id !== to.id) return { kind: "move", toSectionId: to.id, order };
  return order.every((id, i) => id === current[i]) ? null : { kind: "reorder", sectionId: to.id, order };
}

/** `ids` with `id` moved directly before `beforeId`. The create endpoints only append, so an insert is create + reorder. */
export function placeBefore(ids: number[], id: number, beforeId: number): number[] {
  const order = ids.filter((other) => other !== id);
  const index = order.indexOf(beforeId);
  order.splice(index < 0 ? order.length : index, 0, id);
  return order;
}
